/**
 * Services index page.
 *
 * Lists exactly the services with authored content (PUBLISHED_SERVICE_SLUGS).
 * Deriving the list from the content registry rather than from a hardcoded array
 * means this page can never link to a service page that does not exist, and a
 * newly authored service appears here automatically.
 */

import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { PUBLISHED_SERVICE_SLUGS, getService } from "@/content/services";
import { clinicNode } from "@/lib/schema";
import { localizedPath } from "@/lib/locale";
import { motion } from "framer-motion";

interface ServicesIndexPageProps {
  locale: Locale;
}

export default function ServicesIndexPage({ locale }: ServicesIndexPageProps) {
  const { t } = useLanguage();

  const title = locale === "ar" ? "خدماتنا" : "Our Services";
  const description =
    locale === "ar"
      ? "استكشف خدماتنا الشاملة في طب الأسنان. متخصصون في زراعة الأسنان والتقويم وتجميل الأسنان والمزيد"
      : "Explore our comprehensive dental services including implants, orthodontics, cosmetic dentistry and more";

  const allServicesToDisplay = [...PUBLISHED_SERVICE_SLUGS];

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/services`}
        locale={locale}
        schema={clinicNode(locale)}
      />
      <main className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">{title}</h1>
          <p className="text-lg text-slate-300 mb-12 max-w-2xl">
            {description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allServicesToDisplay.map((slug) => {
            const service = getService(slug);
            const serviceName = service
              ? service.name[locale]
              : slug
                  .split("-")
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(" ");
            const summary = service?.summary[locale] || "";

            return (
              <motion.a
                key={slug}
                href={localizedPath(locale, `/services/${slug}`)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                className="group block p-6 rounded-xl border border-slate-700 bg-slate-900/50 hover:border-primary/50 hover:bg-slate-900/80 transition-all duration-300"
              >
                <h2 className="text-xl font-semibold mb-3 text-white group-hover:text-primary transition-colors">
                  {serviceName}
                </h2>
                <p className="text-slate-400 line-clamp-3">
                  {summary || "Learn more about this service."}
                </p>
              </motion.a>
            );
          })}
        </div>
      </main>
    </>
  );
}
